/**
 * Локальные уведомления — напоминания, которые планирует само устройство.
 *
 * Закрывают большую часть сценариев Kairo: приём лекарства, привычка,
 * приближающийся дедлайн, начало запланированной задачи. Ключевое свойство —
 * **сервер для них не нужен вообще**. Это и проще в эксплуатации, и лучше для
 * приватности: расписание лекарств не покидает устройство.
 *
 * Реализаций две, и разница между ними принципиальна.
 *
 * **Нативная оболочка** (`@capacitor/local-notifications`) — настоящее
 * планирование: расписание хранит операционная система, она же будит
 * приложение. Напоминание придёт, даже если приложение выгружено из памяти
 * неделю назад.
 *
 * **Веб** — таймер в открытой вкладке. Переносимого API отложенных уведомлений
 * в браузере нет: `Notification Triggers` так и не вышел за пределы эксперимента
 * в Chromium, а Web Push требует сервера и постоянного соединения. Поэтому в
 * браузере напоминание придёт, только если приложение открыто. Это ограничение
 * не скрывается от пользователя — `describeReliability` возвращает честное
 * описание, которое показывается в настройках.
 */

import { Capacitor } from '@capacitor/core';

import type { IsoDate, MinutesOfDay } from '@/shared/lib/datetime';
import { momentAt } from '@/shared/lib/datetime';

export type NotificationCategory = 'medication' | 'habit' | 'task' | 'deadline' | 'routine';

export interface ScheduledNotification {
  /** Стабильный идентификатор: позволяет заменить или отменить напоминание. */
  id: string;
  title: string;
  body: string;
  category: NotificationCategory;
  date: IsoDate;
  timeMin: MinutesOfDay;
  /** Ссылка внутри приложения, куда ведёт нажатие на уведомление. */
  route?: string;
  /**
   * О чём напоминание: приём или привычка.
   *
   * По нему кнопка в уведомлении делает своё дело, не открывая приложение.
   */
  target?: { kind: 'dose' | 'habit'; id: string; date: IsoDate };
}

/**
 * Кнопки в уведомлении.
 *
 * Напоминание без кнопок требует открыть приложение, найти нужный экран и
 * только там сказать «принял» — а оно застаёт человека в дороге, за рулём, с
 * ребёнком на руках. Ответ в одно нажатие прямо из шторки — то, ради чего
 * напоминание и существует; без него отметка ставится задним числом или не
 * ставится вовсе.
 *
 * Набор кнопок зависит от того, о чём напоминание, поэтому тип действия
 * совпадает с видом напоминания.
 */
export const NOTIFICATION_ACTIONS: Partial<
  Record<NotificationCategory, { id: string; title: string }[]>
> = {
  medication: [
    { id: 'taken', title: 'Принял' },
    { id: 'snooze', title: 'Отложить 15 мин' },
  ],
  habit: [{ id: 'done', title: 'Выполнено' }],
};

/** Что человек нажал: `tap` — само уведомление, иначе кнопка. */
export interface NotificationAction {
  actionId: string;
  route?: string;
  target?: ScheduledNotification['target'];
}

/** Состояние разрешения — то, что нужно показать в настройках. */
export type NotificationPermission = 'granted' | 'denied' | 'default' | 'unsupported';

const isNative = (): boolean => Capacitor.isNativePlatform();

/**
 * Проверяется само значение, а не наличие ключа в `window`: свойство может
 * присутствовать и быть пустым — так ведут себя встроенные веб-представления
 * без поддержки уведомлений. Проверка `'Notification' in window` в таком
 * окружении отвечала «поддерживается», и следующая же строка падала.
 */
function supportsNotifications(): boolean {
  return typeof window !== 'undefined' && typeof window.Notification !== 'undefined';
}

/* ─────────────────────────── Разрешение ─────────────────────────── */

export async function getPermission(): Promise<NotificationPermission> {
  if (isNative()) {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    const { display } = await LocalNotifications.checkPermissions();
    return display === 'prompt' || display === 'prompt-with-rationale' ? 'default' : display;
  }

  if (!supportsNotifications()) return 'unsupported';
  return Notification.permission;
}

/**
 * Запрашивает разрешение.
 *
 * Вызывать это можно только из обработчика действия пользователя: браузеры
 * отклоняют запрос, пришедший сам по себе при загрузке страницы, а человек,
 * которому системный диалог показали без объяснения, чаще всего жмёт «нет» —
 * и вернуть разрешение потом можно только через настройки браузера.
 */
export async function requestPermission(): Promise<NotificationPermission> {
  if (isNative()) {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    const { display } = await LocalNotifications.requestPermissions();
    return display === 'prompt' || display === 'prompt-with-rationale' ? 'default' : display;
  }

  if (!supportsNotifications()) return 'unsupported';
  if (Notification.permission !== 'default') return Notification.permission;
  return Notification.requestPermission();
}

/** Активны ли уведомления: разрешение выдано и окружение их поддерживает. */
export async function isEnabled(): Promise<boolean> {
  return (await getPermission()) === 'granted';
}

/**
 * Насколько надёжны напоминания в текущем окружении.
 *
 * Текст идёт прямо в настройки: обещание, которое приложение не может
 * выполнить, хуже отсутствия обещания.
 */
export function describeReliability(): string {
  if (isNative()) {
    return 'Напоминания планирует система — придут, даже если приложение закрыто.';
  }
  return 'В браузере напоминание придёт, только пока приложение открыто. Для напоминаний при закрытом приложении установите Kairo на телефон.';
}

/* ─────────────────────────── Планирование ─────────────────────────── */

/**
 * Планирует напоминание. Повторный вызов с тем же `id` заменяет прежнее — это
 * и есть способ перенести напоминание, не размножив его.
 */
export async function schedule(notification: ScheduledNotification): Promise<void> {
  if (!(await isEnabled())) return;

  const when = momentAt(notification.date, notification.timeMin);

  // Прошедшие моменты пропускаем: напоминание о вчерашней дозе бессмысленно и
  // раздражает. Заодно это защищает нативный планировщик, который на дату в
  // прошлом показывает уведомление немедленно.
  if (when <= Date.now()) return;

  if (isNative()) {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    await registerActions();

    await LocalNotifications.schedule({
      notifications: [
        {
          id: numericId(notification.id),
          title: notification.title,
          body: notification.body,
          schedule: { at: new Date(when), allowWhileIdle: true },
          // Тип действия совпадает с видом напоминания: кнопки у приёма и у
          // привычки разные, а регистрируются они один раз на всё приложение.
          actionTypeId: NOTIFICATION_ACTIONS[notification.category] ? notification.category : '',
          extra: {
            route: notification.route ?? '/',
            key: notification.id,
            target: notification.target ?? null,
          },
        },
      ],
    });
    return;
  }

  scheduleInTab(notification, when - Date.now());
}

/**
 * Веб-запасной вариант: таймер в живой вкладке.
 *
 * Дальше суток таймер держать нет смысла — вкладка столько не живёт, а
 * пересборка расписания при каждом возвращении в приложение всё равно заведёт
 * его заново.
 */
function scheduleInTab(notification: ScheduledNotification, delay: number): void {
  if (delay > 24 * 60 * 60 * 1000) return;

  cancelInTab(notification.id);
  timers.set(
    notification.id,
    setTimeout(() => {
      timers.delete(notification.id);
      void show(notification);
    }, delay),
  );
}

const timers = new Map<string, ReturnType<typeof setTimeout>>();

export async function cancel(id: string): Promise<void> {
  cancelInTab(id);

  if (isNative()) {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    await LocalNotifications.cancel({ notifications: [{ id: numericId(id) }] });
  }
}

function cancelInTab(id: string): void {
  const timer = timers.get(id);
  if (timer !== undefined) {
    clearTimeout(timer);
    timers.delete(id);
  }
}

export async function cancelAll(): Promise<void> {
  for (const timer of timers.values()) clearTimeout(timer);
  timers.clear();

  if (isNative()) {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    const { notifications } = await LocalNotifications.getPending();
    if (notifications.length > 0) await LocalNotifications.cancel({ notifications });
  }
}

/** Показывает уведомление немедленно. */
export async function show(notification: ScheduledNotification): Promise<void> {
  if (!(await isEnabled())) return;

  const options: NotificationOptions & { renotify?: boolean } = {
    body: notification.body,
    icon: '/icons/icon-192.png',
    badge: '/icons/icon-192.png',
    tag: notification.id,
    data: { route: notification.route ?? '/' },
    // Одно и то же напоминание не должно накапливаться стопкой.
    renotify: false,
  };

  /*
    В приложении уведомление показывает система, а не браузер.

    Путь через service worker здесь не работает вовсе: разрешение на веб-
    уведомления во встроенном браузере не запрашивается, и вызов молча ничего
    не делает — именно так «Показать пробное уведомление» и оказалось кнопкой
    без последствий. Системе оно отдаётся тем же способом, что и отложенные, —
    только без времени, то есть сейчас.
  */
  if (isNative()) {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    await registerActions();

    await LocalNotifications.schedule({
      notifications: [
        {
          id: numericId(notification.id),
          title: notification.title,
          body: notification.body,
          actionTypeId: NOTIFICATION_ACTIONS[notification.category] ? notification.category : '',
          extra: {
            route: notification.route ?? '/',
            key: notification.id,
            target: notification.target ?? null,
          },
        },
      ],
    });
    return;
  }

  // Через service worker уведомление переживает закрытие вкладки и умеет
  // открывать приложение по нажатию.
  if ('serviceWorker' in navigator) {
    const registration = await navigator.serviceWorker.ready;
    await registration.showNotification(notification.title, options);
    return;
  }

  new Notification(notification.title, options);
}

/* ─────────────────────────── Действия ─────────────────────────── */

let actionsRegistered = false;

/**
 * Объявляет системе кнопки уведомлений.
 *
 * Один раз за запуск: система хранит набор сама, а повторная регистрация
 * ничего не меняет. Ошибку глушим — приложение обязано работать и с
 * уведомлениями без кнопок.
 */
async function registerActions(): Promise<void> {
  if (!isNative() || actionsRegistered) return;
  actionsRegistered = true;

  try {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    await LocalNotifications.registerActionTypes({
      types: Object.entries(NOTIFICATION_ACTIONS).map(([id, actions]) => ({
        id,
        actions: (actions ?? []).map((action) => ({ ...action, destructive: false })),
      })),
    });
  } catch {
    // Кнопок не будет — напоминание всё равно придёт и откроет приложение.
  }
}

/**
 * Подписка на нажатие по уведомлению и по его кнопкам.
 *
 * Нажатие по самому уведомлению приходит с `actionId: 'tap'` — это «открой
 * мне то, о чём напомнили». Кнопки приходят своими идентификаторами и
 * выполняются, не открывая экран.
 */
export function onNotificationAction(handler: (action: NotificationAction) => void): () => void {
  if (!isNative()) return () => undefined;

  const subscription = (async () => {
    const { LocalNotifications } = await import('@capacitor/local-notifications');
    await registerActions();

    return LocalNotifications.addListener('localNotificationActionPerformed', (event) => {
      const extra = (event.notification.extra ?? {}) as {
        route?: string;
        target?: ScheduledNotification['target'];
      };

      handler({
        actionId: event.actionId,
        route: extra.route,
        target: extra.target ?? undefined,
      });
    });
  })();

  return () => {
    void subscription.then((listener) => listener.remove()).catch(() => undefined);
  };
}

/* ─────────────────────────── Служебное ─────────────────────────── */

/**
 * Нативный планировщик адресует уведомления числами, а вся модель приложения
 * держится на строковых идентификаторах. Хеш переводит одно в другое
 * устойчиво: тот же ключ всегда даёт то же число, поэтому перепланирование
 * заменяет напоминание, а не добавляет второе.
 *
 * Диапазон ограничен положительным int32 — этого требует Android.
 */
export function numericId(key: string): number {
  let hash = 2166136261;
  for (let index = 0; index < key.length; index += 1) {
    hash ^= key.charCodeAt(index);
    hash = Math.imul(hash, 16777619);
  }
  return (hash >>> 0) % 2147483647;
}
